import { motion } from "framer-motion";

/**
 * H.E.A.R.T brand mark — Healthcare Earned Advanced Rebate Tracking.
 * Geometric ECG trace that resolves into a rising trend line: the product
 * tracks clinical activity and turns it into recovered money.
 */
export function HeartMark({ size = 56, className = "", idSuffix = "a" }) {
  const g1 = `heart-mark-grad-${idSuffix}`;
  const g2 = `heart-mark-glow-${idSuffix}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label="H.E.A.R.T"
      className={className}
      data-testid="heart-logo-mark"
    >
      <defs>
        <linearGradient id={g1} x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0b2a45" />
          <stop offset="0.55" stopColor="#0e4f6e" />
          <stop offset="1" stopColor="#0891b2" />
        </linearGradient>
        <linearGradient id={g2} x1="8" y1="44" x2="56" y2="16" gradientUnits="userSpaceOnUse">
          <stop stopColor="#e0f2fe" />
          <stop offset="1" stopColor="#67e8f9" />
        </linearGradient>
      </defs>

      <rect x="0.75" y="0.75" width="62.5" height="62.5" rx="15" fill={`url(#${g1})`} />
      <rect x="0.75" y="0.75" width="62.5" height="62.5" rx="15" stroke="#22d3ee" strokeOpacity="0.35" strokeWidth="1.5" />

      {/* ECG trace */}
      <path
        d="M9 38H17.5L21 30.5L26 45L31.5 21L35.5 38H41"
        stroke={`url(#${g2})`}
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* rising recovery arm */}
      <path
        d="M41 38L54 20"
        stroke="#5eead4"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M46.5 19.5H54.5V27.5"
        stroke="#5eead4"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="38" r="2.6" fill="#e0f2fe" />
    </svg>
  );
}

/**
 * Full lockup: mark + wordmark + expansion. `tone` switches for light surfaces.
 */
export function HeartLogo({
  size = 56,
  tone = "dark",
  showExpansion = true,
  className = "",
  idSuffix = "b",
}) {
  const word = tone === "dark" ? "text-white" : "text-slate-900";
  const sub = tone === "dark" ? "text-cyan-300/80" : "text-cyan-700";
  return (
    <div className={`flex items-center gap-4 ${className}`} data-testid="heart-logo-lockup">
      <HeartMark size={size} idSuffix={idSuffix} />
      <div className="min-w-0">
        <div className={`font-display font-black leading-none tracking-[0.12em] ${word}`} style={{ fontSize: size * 0.5 }}>
          H.E.A.R.T
        </div>
        {showExpansion && (
          <div className={`mt-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.16em] ${sub}`}>
            Healthcare Earned Advanced Rebate Tracking
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Branded panel used in place of a stock photo on the software capability tile.
 */
export function HeartLogoPanel({ className = "" }) {
  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-[#061124] ${className}`}
      data-testid="heart-logo-panel"
    >
      {/* grid + glow field */}
      <div
        className="absolute inset-0 opacity-[0.22]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(34,211,238,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(34,211,238,0.35) 1px, transparent 1px)",
          backgroundSize: "34px 34px",
        }}
      />
      <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-cyan-500/20 blur-[70px]" />
      <div className="absolute -left-12 bottom-[-60px] h-48 w-48 rounded-full bg-teal-400/10 blur-[60px]" />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 flex h-full flex-col justify-end px-5 pb-5 pt-16 lg:px-6"
      >
        <HeartLogo size={48} idSuffix="tile" />
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-cyan-200">
            Rebate recovery
          </span>
          <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-slate-300">
            Bill-only capture
          </span>
          <span className="hidden sm:inline-flex rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-slate-300">
            Inventory
          </span>
        </div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#061124] to-transparent" />
    </div>
  );
}

export default HeartLogo;
